import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, FileSignature, Activity, Wallet, ShieldCheck, XCircle } from 'lucide-react';

const Subcontracts = () => {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [subs, setSubs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    subcontractor_id: '', project_id: '', title: '', scope_of_work: '',
    contract_value: 0, vat_pct: 15, retention_pct: 5, advance_pct: 0,
    start_date: '', end_date: '', notes: '',
  });

  // Lifecycle modal state
  const [lifecycleId, setLifecycleId] = useState(null);
  const [lifecycle, setLifecycle] = useState(null);
  const [tab, setTab] = useState('overview'); // overview | pay | retention | close
  const today = new Date().toISOString().slice(0, 10);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: today, payment_method: 'bank', reference: '', notes: '' });
  const [retForm, setRetForm] = useState({ amount: '', release_date: today, payment_method: 'bank', reference: '', notes: '' });
  const [closeForm, setCloseForm] = useState({ close_date: today, settlement_type: 'normal', write_off_amount: 0, notes: '' });

  const load = async () => {
    try {
      const [a, b, c] = await Promise.all([
        axiosInstance.get('/subcontracts'),
        axiosInstance.get('/subcontractors'),
        axiosInstance.get('/projects'),
      ]);
      setList(a.data || []); setSubs(b.data || []); setProjects(c.data || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/subcontracts', { ...form, contract_value: Number(form.contract_value) });
      toast.success('Subcontract created');
      setShow(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const openLifecycle = async (id) => {
    setLifecycleId(id); setTab('overview'); setLifecycle(null);
    try {
      const r = await axiosInstance.get(`/subcontracts/${id}/lifecycle`);
      setLifecycle(r.data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to load lifecycle'); }
  };
  const refreshLifecycle = async () => {
    if (!lifecycleId) return;
    const r = await axiosInstance.get(`/subcontracts/${lifecycleId}/lifecycle`);
    setLifecycle(r.data);
    load();
  };
  const closeLifecycle = () => { setLifecycleId(null); setLifecycle(null); };

  const submitPay = async (e) => {
    e.preventDefault();
    try {
      const r = await axiosInstance.post(`/subcontracts/${lifecycleId}/pay`, { ...payForm, amount: Number(payForm.amount) });
      toast.success(`${r.data.payment_type === 'full' ? 'Full' : 'Partial'} payment posted — ${r.data.journal_no}`);
      setPayForm({ amount: '', payment_date: today, payment_method: 'bank', reference: '', notes: '' });
      await refreshLifecycle();
      setTab('overview');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Payment failed'); }
  };

  const submitRetention = async (e) => {
    e.preventDefault();
    try {
      const r = await axiosInstance.post(`/subcontracts/${lifecycleId}/release-retention`, { ...retForm, amount: Number(retForm.amount) });
      toast.success(`Retention released — ${r.data.journal_no}`);
      setRetForm({ amount: '', release_date: today, payment_method: 'bank', reference: '', notes: '' });
      await refreshLifecycle();
      setTab('overview');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Release failed'); }
  };

  const submitClose = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...closeForm };
      payload.write_off_amount = Number(payload.write_off_amount || 0);
      const r = await axiosInstance.post(`/subcontracts/${lifecycleId}/close`, payload);
      toast.success(`Subcontract ${r.data.new_status}${r.data.journal_no ? ` — ${r.data.journal_no}` : ''}`);
      await refreshLifecycle();
      setTab('overview');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Close failed'); }
  };

  const s = lifecycle?.summary;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('subcontracts.title')}</h1>
        <button data-testid="add-subcontract-btn" onClick={() => setShow(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold">
          <Plus size={18} /> {t('subcontracts.new')}
        </button>
      </div>
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left">{t('subcontracts.fields.contract_no')}</th>
              <th className="px-4 py-3 text-left">{t('subcontracts.fields.subcontractor')}</th>
              <th className="px-4 py-3 text-left">{t('subcontracts.fields.project')}</th>
              <th className="px-4 py-3 text-left">{t('subcontracts.fields.title')}</th>
              <th className="px-4 py-3 text-left">{t('subcontracts.fields.value')}</th>
              <th className="px-4 py-3 text-left">{t('subcontracts.fields.claimed')}</th>
              <th className="px-4 py-3 text-left">{t('subcontracts.fields.retention')}</th>
              <th className="px-4 py-3 text-left">{t('common.status')}</th>
              <th className="px-4 py-3 text-left">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-3 font-mono">{c.contract_no}</td>
                <td className="px-4 py-3">{c.subcontractor_name}</td>
                <td className="px-4 py-3">{c.project_name}</td>
                <td className="px-4 py-3">{c.title}</td>
                <td className="px-4 py-3 font-mono">{c.contract_value?.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-orange-700">{c.total_claimed?.toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-blue-700">{c.retention_held?.toLocaleString()}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${c.status === 'active' ? 'bg-emerald-100 text-emerald-800' : c.status === 'completed' ? 'bg-blue-100 text-blue-800' : c.status === 'terminated' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>{c.status}</span>
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => openLifecycle(c.id)}
                    data-testid={`lifecycle-${c.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold"
                  >
                    <Activity size={12} /> {t('subcontracts.lifecycle', { defaultValue: 'Lifecycle' })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="p-6 border-b flex items-center gap-2"><FileSignature size={20} /><h2 className="text-xl font-bold">{t('subcontracts.create')}</h2></div>
            <form onSubmit={save} className="p-6 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Subcontractor *</label>
                <select required value={form.subcontractor_id} onChange={(e) => setForm({ ...form, subcontractor_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="form-sub">
                  <option value="">— Select —</option>
                  {subs.map((sub) => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Project *</label>
                <select required value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="form-project">
                  <option value="">— Select —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600 mb-1 block">Title *</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600 mb-1 block">Scope of Work</label>
                <textarea value={form.scope_of_work} onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })} rows="2" className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Value (SAR) *</label>
                <input type="number" required step="0.01" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">VAT %</label>
                <input type="number" value={form.vat_pct} onChange={(e) => setForm({ ...form, vat_pct: Number(e.target.value) })} className="w-full border rounded px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Retention %</label>
                <input type="number" value={form.retention_pct} onChange={(e) => setForm({ ...form, retention_pct: Number(e.target.value) })} className="w-full border rounded px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Advance %</label>
                <input type="number" value={form.advance_pct} onChange={(e) => setForm({ ...form, advance_pct: Number(e.target.value) })} className="w-full border rounded px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Start Date *</label>
                <input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">End Date</label>
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
              <div className="col-span-2 flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-lg border text-sm">{t('common.cancel')}</button>
                <button type="submit" className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold text-sm" data-testid="save-subcontract-btn">{t('common.save')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lifecycle modal */}
      {lifecycleId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" data-testid="lifecycle-modal">
            <div className="p-6 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity size={20} />
                <h2 className="text-xl font-bold">
                  {lifecycle?.subcontract?.contract_no || ''} — {lifecycle?.subcontract?.subcontractor_name || ''}
                </h2>
              </div>
              <button onClick={closeLifecycle} className="p-1 hover:bg-gray-100 rounded" data-testid="lifecycle-close"><XCircle size={20} /></button>
            </div>

            {!lifecycle ? (
              <div className="p-12 text-center text-gray-500">Loading…</div>
            ) : (
              <>
                {/* Summary cards */}
                <div className="px-6 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-gray-50 border rounded-lg p-3"><div className="text-xs text-gray-500">Contract Value</div><div className="font-mono font-bold">{s.contract_value?.toLocaleString()}</div></div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3"><div className="text-xs text-emerald-700">Total Paid</div><div className="font-mono font-bold text-emerald-800" data-testid="lc-paid">{s.total_paid?.toLocaleString()}</div></div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3"><div className="text-xs text-orange-700">Outstanding</div><div className="font-mono font-bold text-orange-800" data-testid="lc-outstanding">{s.outstanding?.toLocaleString()}</div></div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3"><div className="text-xs text-blue-700">Retention Held</div><div className="font-mono font-bold text-blue-800" data-testid="lc-retention">{s.retention_held?.toLocaleString()}</div></div>
                </div>

                {/* Tabs */}
                <div className="px-6 mt-6 border-b flex gap-2 text-sm">
                  {[
                    { k: 'overview', label: 'Overview' },
                    { k: 'pay', label: 'Pay', icon: Wallet, disabled: s.outstanding < 0.01 },
                    { k: 'retention', label: 'Release Retention', icon: ShieldCheck, disabled: s.retention_held < 0.01 },
                    { k: 'close', label: 'Close', icon: XCircle, disabled: !s.can_close && s.status === 'active' && false },
                  ].map((tk) => (
                    <button
                      key={tk.k}
                      onClick={() => setTab(tk.k)}
                      disabled={tk.disabled}
                      data-testid={`tab-${tk.k}`}
                      className={`px-3 py-2 border-b-2 font-medium ${tab === tk.k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-600 hover:text-gray-900'} ${tk.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                    >{tk.label}</button>
                  ))}
                </div>

                <div className="p-6">
                  {tab === 'overview' && (
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold mb-2 text-sm text-gray-700">Approved Claims</h3>
                        {(lifecycle.claims || []).length === 0 ? (
                          <div className="text-xs text-gray-500 italic">No approved claims yet.</div>
                        ) : (
                          <table className="w-full text-xs border">
                            <thead className="bg-gray-50"><tr><th className="px-2 py-2 text-left">Claim #</th><th className="px-2 py-2 text-right">Net Payable</th><th className="px-2 py-2 text-right">Retention</th><th className="px-2 py-2 text-left">Date</th></tr></thead>
                            <tbody>
                              {lifecycle.claims.map((c, i) => (
                                <tr key={i} className="border-t">
                                  <td className="px-2 py-1 font-mono">{c.claim_no}</td>
                                  <td className="px-2 py-1 text-right font-mono">{c.net_payable?.toLocaleString()}</td>
                                  <td className="px-2 py-1 text-right font-mono">{c.retention_amount?.toLocaleString()}</td>
                                  <td className="px-2 py-1">{(c.created_at || '').slice(0, 10)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2 text-sm text-gray-700">Payments & Releases</h3>
                        {(lifecycle.payments || []).length === 0 ? (
                          <div className="text-xs text-gray-500 italic">No payments yet.</div>
                        ) : (
                          <table className="w-full text-xs border" data-testid="lc-payments-table">
                            <thead className="bg-gray-50"><tr><th className="px-2 py-2 text-left">Payment #</th><th className="px-2 py-2 text-left">Type</th><th className="px-2 py-2 text-right">Amount</th><th className="px-2 py-2 text-left">Method</th><th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Journal</th></tr></thead>
                            <tbody>
                              {lifecycle.payments.map((p, i) => (
                                <tr key={i} className="border-t">
                                  <td className="px-2 py-1 font-mono">{p.payment_no}</td>
                                  <td className="px-2 py-1">
                                    <span className={`px-2 py-0.5 rounded text-[10px] ${p.payment_type === 'retention_release' ? 'bg-blue-100 text-blue-800' : p.payment_type === 'full' ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'}`}>{p.payment_type}</span>
                                  </td>
                                  <td className="px-2 py-1 text-right font-mono">{p.amount?.toLocaleString()}</td>
                                  <td className="px-2 py-1">{p.payment_method}</td>
                                  <td className="px-2 py-1">{p.payment_date}</td>
                                  <td className="px-2 py-1 font-mono text-indigo-600">{p.journal_no}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}

                  {tab === 'pay' && (
                    <form onSubmit={submitPay} className="grid grid-cols-2 gap-4 max-w-2xl">
                      <div className="col-span-2 text-xs text-gray-600">Outstanding: <strong className="font-mono text-orange-700">{s.outstanding?.toLocaleString()}</strong></div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Amount *</label>
                        <input data-testid="pay-amount" type="number" step="0.01" required value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" max={s.outstanding} />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Date *</label>
                        <input type="date" required value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Method</label>
                        <select value={payForm.payment_method} onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                          <option value="bank">Bank</option><option value="cash">Cash</option><option value="cheque">Cheque</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Reference</label>
                        <input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-600 mb-1 block">Notes</label>
                        <textarea rows="2" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="col-span-2 flex justify-end gap-2">
                        <button type="button" onClick={() => setTab('overview')} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
                        <button type="submit" data-testid="pay-submit" className="px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold text-sm flex items-center gap-2">
                          <Wallet size={14} /> Post Payment
                        </button>
                      </div>
                    </form>
                  )}

                  {tab === 'retention' && (
                    <form onSubmit={submitRetention} className="grid grid-cols-2 gap-4 max-w-2xl">
                      <div className="col-span-2 text-xs text-gray-600">Retention Held: <strong className="font-mono text-blue-700">{s.retention_held?.toLocaleString()}</strong></div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Amount to Release *</label>
                        <input data-testid="ret-amount" type="number" step="0.01" required value={retForm.amount} onChange={(e) => setRetForm({ ...retForm, amount: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" max={s.retention_held} />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Release Date *</label>
                        <input type="date" required value={retForm.release_date} onChange={(e) => setRetForm({ ...retForm, release_date: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Method</label>
                        <select value={retForm.payment_method} onChange={(e) => setRetForm({ ...retForm, payment_method: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                          <option value="bank">Bank</option><option value="cash">Cash</option><option value="cheque">Cheque</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Reference</label>
                        <input value={retForm.reference} onChange={(e) => setRetForm({ ...retForm, reference: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-gray-600 mb-1 block">Notes</label>
                        <textarea rows="2" value={retForm.notes} onChange={(e) => setRetForm({ ...retForm, notes: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="col-span-2 flex justify-end gap-2">
                        <button type="button" onClick={() => setTab('overview')} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
                        <button type="submit" data-testid="ret-submit" className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold text-sm flex items-center gap-2">
                          <ShieldCheck size={14} /> Release Retention
                        </button>
                      </div>
                    </form>
                  )}

                  {tab === 'close' && (
                    <form onSubmit={submitClose} className="grid grid-cols-2 gap-4 max-w-2xl">
                      <div className="col-span-2 text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded p-3">
                        Outstanding: <strong className="font-mono">{s.outstanding?.toLocaleString()}</strong> · Retention: <strong className="font-mono">{s.retention_held?.toLocaleString()}</strong>
                        <div className="mt-1 text-[11px] text-amber-700">Normal close only works when both are 0. Use Mutual Agreement / Terminated with a write-off amount otherwise.</div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Close Date *</label>
                        <input type="date" required value={closeForm.close_date} onChange={(e) => setCloseForm({ ...closeForm, close_date: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-gray-600 mb-1 block">Settlement Type *</label>
                        <select data-testid="close-type" value={closeForm.settlement_type} onChange={(e) => setCloseForm({ ...closeForm, settlement_type: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                          <option value="normal">Normal</option>
                          <option value="mutual_agreement">Mutual Agreement</option>
                          <option value="terminated">Terminated</option>
                        </select>
                      </div>
                      {closeForm.settlement_type !== 'normal' && (
                        <div className="col-span-2">
                          <label className="text-xs text-gray-600 mb-1 block">Write-off Amount *</label>
                          <input type="number" step="0.01" required value={closeForm.write_off_amount} onChange={(e) => setCloseForm({ ...closeForm, write_off_amount: e.target.value })} className="w-full border rounded px-3 py-2 text-sm font-mono" />
                        </div>
                      )}
                      <div className="col-span-2">
                        <label className="text-xs text-gray-600 mb-1 block">Notes</label>
                        <textarea rows="2" value={closeForm.notes} onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" />
                      </div>
                      <div className="col-span-2 flex justify-end gap-2">
                        <button type="button" onClick={() => setTab('overview')} className="px-4 py-2 rounded-lg border text-sm">Cancel</button>
                        <button type="submit" data-testid="close-submit" className="px-4 py-2 rounded-lg bg-gradient-to-r from-rose-600 to-red-600 text-white font-semibold text-sm flex items-center gap-2">
                          <XCircle size={14} /> Close Subcontract
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Subcontracts;
